import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import {
  AggregateMetricsRequest,
  NumericAggregateMetricsResponse,
} from "../MetricRecord";

import { metricsQueryKeys } from "./metricsQueryKeys";

/**
 * Aggregate a metric across sessions, grouped by calendar period. Backs the
 * top-level / device dashboard's chart cards.
 */
export function useAggregateMetrics(
  request: AggregateMetricsRequest | undefined,
  orgId: string | undefined,
) {
  const { metricsService } = useDomainServices();

  return useQuery<NumericAggregateMetricsResponse, Error>({
    queryKey: metricsQueryKeys.aggregate(request, orgId),
    queryFn: ({ signal }) => {
      if (request === undefined) {
        return Promise.reject(new Error("Missing aggregate request"));
      }
      return metricsService.aggregateMetric(request, {
        abortSignal: signal,
        orgId,
      });
    },
    enabled: request !== undefined && orgId !== undefined,
    staleTime: 60_000,
    // Keep the prior series rendered while a new query key (a changed filter,
    // range, or period) loads, so cards transition smoothly instead of all
    // flashing to a spinner at once.
    placeholderData: keepPreviousData,
  });
}
